<template>
  <div class="withdraw">
    <h1>Withdraw Points</h1>
    <h3>
      Current balance: {{ balance }} Sweeps Coins ({{ availableDollars }})
    </h3>
    <div v-if="showOptions" class="withdrawal-options">
      <p>Select withdrawal amount:</p>
      <div class="amount-buttons">
        <button
          v-for="option in withdrawalOptions"
          :key="option.label"
          @click="selectAmount(option)"
          :disabled="option.disabled"
          :class="{
            'amount-button': true,
            selected: selectedOption === option.label,
            disabled: option.disabled,
          }"
        >
          {{ option.label }}<br />
          <small>{{ option.subText }}</small>
        </button>
      </div>
      <button
        @click="handleWithdraw"
        :disabled="!selectedOption || loading"
        class="withdraw-button"
      >
        {{
          loading
            ? "Processing..."
            : selectedOption
            ? `Withdraw ${
                selectedOption === "All"
                  ? "$" + amount.toFixed(2)
                  : selectedOption
              }`
            : "Select Amount to Withdraw"
        }}
      </button>
    </div>
    <p v-if="message">{{ message }}</p>
    <div id="container"></div>

    <div class="operator-link">
      <h2>Operator approvals:</h2>
      <p>
        <router-link to="/operator">Click here</router-link> to view a simulated
        approval screen for pending disbursements.
      </p>
      <p>
        This would typically be done within a back office/admin portal built by
        the operator.
      </p>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { createDisbursementIntent } from "../api/vivamo-api";

export default {
  setup() {
    const store = useStore();
    const message = ref("");
    const points = ref(0);
    const amount = ref(0);
    const loading = ref(false);
    const success = ref(false);
    const selectedOption = ref(null);
    const showOptions = ref(true);

    const balance = computed(() => store.state.sweepsCoinsBalance);
    const availableDollars = computed(
      () => `$${(balance.value * 0.01).toFixed(2)}`
    );

    const withdrawalOptions = computed(() => {
      const availableAmount = balance.value * 0.01;
      return [
        {
          label: "$10",
          subText: "(1,000 SC)",
          amount: 10,
          points: 1000,
          disabled: availableAmount < 10,
        },
        {
          label: "$50",
          subText: "(5,000 SC)",
          amount: 50,
          points: 5000,
          disabled: availableAmount < 50,
        },
        {
          label: "$100",
          subText: "(10,000 SC)",
          amount: 100,
          points: 10000,
          disabled: availableAmount < 100,
        },
        {
          label: "All",
          subText: `(${balance.value.toLocaleString()} SC)`,
          amount: availableAmount,
          points: balance.value,
          disabled: balance.value === 0,
        },
      ];
    });

    const selectAmount = (option) => {
      if (!option.disabled) {
        selectedOption.value = option.label;
        points.value = option.points;
        amount.value = option.amount;
      }
    };

    const handleWithdraw = async () => {
      if (selectedOption.value && points.value > 0) {
        loading.value = true;
        message.value = "Loading...";

        // Ensure Vivamo is initialized
        if (!store.state.vivamo) {
          await store.dispatch("initializeVivamo");
        }

        if (!store.state.vivamo) {
          message.value = "Error: Payment SDK not available";
          loading.value = false;
          return;
        }

        // Get logged-in user's data from localStorage
        const userData = JSON.parse(
          localStorage.getItem("demoUserData") || "{}"
        );
        const userId = userData.username || "demo-user";

        const disbursementIntentData = {
          customer: {
            externalCustomerId: userId,
          },
          disbursementIntent: {
            externalDisbursementIntentId: `demo_${Math.floor(Date.now())}`,
            amount: `${amount.value}`,
            currency: "USD",
          },
        };

        try {
          const response = await createDisbursementIntent(
            disbursementIntentData
          );
          showOptions.value = false;
          message.value = "Please follow the disbursement instructions below:";
          success.value = true;
          console.log("Disbursement intent created:", response);

          // Call the Vivamo createElements method with the clientSecretKey
          store.state.vivamo.createElements(
            "#container",
            response.clientSecretKey
          );
          store.state.vivamo.on("disbursement-created", (data) => {
            console.log("Disbursement created: ", data);
            message.value = "";
            store.dispatch("withdraw", {
              coinType: "sweeps",
              points: points.value,
            });
          });
        } catch (error) {
          message.value = "Error creating disbursement intent.";
          console.error("Error creating disbursement intent:", error);
          showOptions.value = true;
        } finally {
          loading.value = false;
        }
      } else {
        message.value = "Please select a withdrawal amount.";
      }
    };

    onMounted(async () => {
      await store.dispatch("initializeVivamo");
    });

    return {
      handleWithdraw,
      selectAmount,
      points,
      message,
      balance,
      amount,
      loading,
      success,
      selectedOption,
      withdrawalOptions,
      availableDollars,
      showOptions,
    };
  },
};
</script>

<style scoped>
.withdraw {
  max-width: 800px;
  margin: auto;
  text-align: center;
}

.error-message {
  color: red;
}

.withdraw-button {
  margin-top: 10px;
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.withdraw-button:disabled {
  background-color: var(--text-color);
  color: var(--background-color);
  opacity: 0.3;
  cursor: not-allowed;
}

.withdraw-button:hover:not(:disabled) {
  background-color: #0056b3;
}

#container {
  margin-top: 20px;
}
.withdrawal-options {
  margin: 2rem 0;
}

.amount-buttons {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
  margin: 1rem 0;
}

.amount-button {
  padding: 1rem;
  border: 2px solid var(--secondary-color);
  background: transparent;
  color: var(--secondary-color);
  border-radius: 10px;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: bold;
  transition: all 0.3s ease;
}

.amount-button:hover:not(.disabled) {
  background: var(--secondary-color);
  color: white;
}

.amount-button.selected {
  background: var(--secondary-color);
  color: white;
}

.amount-button.disabled {
  border-color: var(--text-color);
  color: var(--text-color);
  opacity: 0.3;
  cursor: not-allowed;
}

.amount-button.disabled:hover {
  background: transparent;
  color: var(--text-color);
  opacity: 0.3;
}

.operator-link {
  margin: 2rem 0;
  font-size: 0.9rem;
  padding: 1rem;
  background-color: var(--accent-color);
  border-radius: 10px;
  color: #2a2a2a;
}

.operator-link a {
  color: #2a2a2a;
  text-decoration: underline;
}

.operator-link a:hover {
  color: var(--secondary-color);
}

/* Mobile responsive styles */
@media (max-width: 768px) {
  .withdraw {
    max-width: 100%;
    padding: 0 1rem;
  }

  .withdraw h1 {
    font-size: 1.8rem;
    margin-bottom: 1rem;
    color: var(--text-color);
  }

  .withdraw h3 {
    font-size: 1.2rem;
    margin-bottom: 1.5rem;
    color: var(--accent-color);
    line-height: 1.4;
  }

  .withdrawal-options {
    margin: 1.5rem 0;
  }

  .withdrawal-options p {
    font-size: 1rem;
    margin-bottom: 1rem;
    color: var(--text-color);
  }

  .amount-buttons {
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 0.8rem;
    margin: 1rem 0;
  }

  .amount-button {
    padding: 0.8rem;
    font-size: 1rem;
    border-radius: 8px;
  }

  .amount-button small {
    font-size: 0.8rem;
  }

  .withdraw-button {
    margin-top: 1rem;
    padding: 0.8rem 1.5rem;
    font-size: 0.9rem;
    border-radius: 20px;
    background-color: var(--secondary-color);
    color: white;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    width: 100%;
    max-width: 300px;
  }

  .withdraw-button:hover:not(:disabled) {
    background-color: var(--accent-color);
    color: var(--primary-color);
  }

  .withdraw-button:disabled {
    background-color: var(--text-color);
    color: var(--background-color);
    opacity: 0.3;
    cursor: not-allowed;
  }

  #container {
    margin-top: 1.5rem;
  }

  .operator-link {
    margin: 1.5rem 0;
    padding: 0.8rem;
    font-size: 0.85rem;
    border-radius: 8px;
  }
}

@media (max-width: 480px) {
  .withdraw {
    padding: 0 0.75rem;
  }

  .withdraw h1 {
    font-size: 1.6rem;
    margin-bottom: 0.8rem;
  }

  .withdraw h3 {
    font-size: 1rem;
    margin-bottom: 1.2rem;
    padding: 0 0.5rem;
  }

  .withdrawal-options {
    margin: 1.2rem 0;
  }

  .withdrawal-options p {
    font-size: 0.9rem;
    margin-bottom: 0.8rem;
  }

  .amount-buttons {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.6rem;
    margin: 0.8rem 0;
  }

  .amount-button {
    padding: 0.7rem 0.5rem;
    font-size: 0.9rem;
    border-radius: 6px;
  }

  .amount-button small {
    font-size: 0.7rem;
  }

  .withdraw-button {
    padding: 0.7rem 1.2rem;
    font-size: 0.85rem;
    margin-top: 0.8rem;
    width: 100%;
    max-width: none;
  }

  #container {
    margin-top: 1.2rem;
  }

  .operator-link {
    margin: 1.2rem 0;
    padding: 0.7rem;
    font-size: 0.8rem;
  }
}

/* Very small screens */
@media (max-width: 360px) {
  .withdraw {
    padding: 0 0.5rem;
  }

  .withdraw h1 {
    font-size: 1.4rem;
  }

  .withdraw h3 {
    font-size: 0.9rem;
    padding: 0;
  }

  .amount-buttons {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }

  .amount-button {
    padding: 0.6rem;
    font-size: 0.85rem;
    max-width: 200px;
    margin: 0 auto;
  }

  .amount-button small {
    font-size: 0.65rem;
  }

  .withdraw-button {
    padding: 0.6rem 1rem;
    font-size: 0.8rem;
  }

  .operator-link {
    padding: 0.6rem;
    font-size: 0.75rem;
  }
}

/* Landscape mobile orientation */
@media (max-width: 768px) and (orientation: landscape) {
  .withdraw h1 {
    font-size: 1.5rem;
    margin-bottom: 0.8rem;
  }

  .withdraw h3 {
    font-size: 1rem;
    margin-bottom: 1rem;
  }

  .withdrawal-options {
    margin: 1rem 0;
  }

  .amount-buttons {
    grid-template-columns: repeat(4, 1fr);
    gap: 0.5rem;
    margin: 0.8rem 0;
  }

  .amount-button {
    padding: 0.5rem;
    font-size: 0.8rem;
  }

  .amount-button small {
    font-size: 0.65rem;
  }

  .withdraw-button {
    padding: 0.6rem 1.2rem;
    font-size: 0.8rem;
    margin-top: 0.8rem;
    max-width: 250px;
  }

  #container {
    margin-top: 1rem;
  }

  .operator-link {
    margin: 1rem 0;
    padding: 0.6rem;
    font-size: 0.75rem;
  }
}

/* Very large mobile screens */
@media (max-width: 768px) and (min-width: 600px) {
  .amount-buttons {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 1rem;
  }

  .amount-button {
    padding: 1rem;
    font-size: 1.05rem;
  }

  .amount-button small {
    font-size: 0.85rem;
  }

  .withdraw-button {
    padding: 0.9rem 1.8rem;
    font-size: 0.95rem;
    max-width: 350px;
  }
}
</style>
